import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import './App.css';

function AppContent() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Determine active tab from current path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    return path.substring(1); // Remove leading slash
  };

  const activeTab = getActiveTab();

  // Navigation handler for child components
  const handleNavigate = (tab) => {
    navigate(tab === 'home' ? '/' : `/${tab}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50">
      {/* Modern Navigation Header */}
      <header className="bg-white/95 backdrop-blur-lg shadow-sm sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            {/* Logo Only */}
            <a href="/" className="flex-shrink-0 group">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-500 blur-lg opacity-0 group-hover:opacity-30 transition-opacity"></div>
                <img src="/images/logo3.svg" alt="Hydroclimate Lab Logo" className="h-20 w-40 md:h-24 md:w-48 relative z-10" />
              </div>
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              <Link
                to="/"
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  activeTab === 'home'
                    ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg shadow-blue-200'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}>
                Home
              </Link>

              {/* About Us Dropdown */}
              <div className="relative group">
                <button
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    activeTab === 'aboutus' || activeTab === 'people'
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg shadow-blue-200'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}>
                  About Us
                </button>
                <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <Link
                    to="/aboutus"
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-t-lg">
                    About Us
                  </Link>
                  <Link
                    to="/people"
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-b-lg">
                    People
                  </Link>
                </div>
              </div>

              {['research', 'publications', 'resources', 'news', 'contact'].map(tab => (
                <Link
                  key={tab}
                  to={`/${tab}`}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    activeTab === tab
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg shadow-blue-200'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}>
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </Link>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-100">
              <nav className="flex flex-col space-y-2">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm text-left transition ${
                    activeTab === 'home' ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                  }`}>
                  Home
                </Link>

                {/* About Us Section */}
                <div className="pl-2">
                  <Link
                    to="/aboutus"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block w-full px-4 py-2 rounded-lg font-medium text-sm text-left transition ${
                      activeTab === 'aboutus' ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                    }`}>
                    About Us
                  </Link>
                  <Link
                    to="/people"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block w-full px-4 py-2 rounded-lg font-medium text-sm text-left transition ml-4 ${
                      activeTab === 'people' ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                    }`}>
                    - People
                  </Link>
                </div>

                {['research', 'publications', 'resources', 'news', 'contact'].map(tab => (
                  <Link
                    key={tab}
                    to={`/${tab}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm text-left transition ${
                      activeTab === tab ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                    }`}>
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </Link>
                ))}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main>
        <Routes>
          <Route path="/" element={<HomePage onNavigate={handleNavigate} />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/aboutus" element={<AboutUsPage />} />
          <Route path="/people" element={<PeoplePage onNavigate={handleNavigate} />} />
          <Route path="/cv" element={<CVPage onNavigate={handleNavigate} />} />
          <Route path="/research" element={<ResearchPage />} />
          <Route path="/publications" element={<PublicationsPage />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Routes>
      </main>

      {/* Footer - only show on home page */}
      {activeTab === 'home' && (
        <footer className="fixed bottom-0 w-full text-center py-4 px-4 bg-white/80 text-[#1296db] text-sm flex justify-center items-center">
          <p>&copy; 2025 Hydroclimate Systems Lab. All Rights Reserved. | <a href="http://beian.miit.gov.cn/" target="_blank" rel="noreferrer" className="text-[#1296db] hover:underline">京ICP备2025117566号-1</a></p>
        </footer>
      )}
    </div>
  );
}

// HomePage Component - Ultra Modern Design
function HomePage({ onNavigate }) {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Animated Gradient Background */}
      <div className="relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-cyan-50 to-slate-50">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-cyan-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        {/* Hero Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-32">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100/80 backdrop-blur-sm border border-blue-200 mb-8">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></span>
              <span className="text-sm font-semibold text-blue-700">Leading Research in Hydroclimate Systems</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold mb-8">
              <span className="block text-gray-900 mb-2">Hydroclimate</span>
              <span className="block bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-500 bg-clip-text text-transparent">
                Systems Lab
              </span>
            </h1>

            {/* Subtitle */}
            <p className="max-w-3xl mx-auto text-xl sm:text-2xl text-gray-600 mb-12 leading-relaxed">
              Pioneering innovative solutions for climate resilience through advanced modeling,
              data analytics, and interdisciplinary research.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => onNavigate('research')}
                className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-105">
                <span className="relative z-10 flex items-center">
                  Explore Our Research
                  <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </button>
              <button
                onClick={() => onNavigate('contact')}
                className="px-8 py-4 bg-white text-gray-900 rounded-xl font-semibold border-2 border-gray-200 hover:border-blue-500 hover:text-blue-600 transition-all duration-300 hover:shadow-lg">
                Contact Our Team
              </button>
            </div>
          </div>

          {/* Hero Image with Modern Card Design */}
          <div className="mt-20 relative mb-24">
            <div className="relative mx-auto max-w-5xl">
              {/* Main Image Container */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
                <img
                  src="/images/bc02.jpg"
                  alt="Hydroclimate Research"
                  className="w-full h-[400px] sm:h-[500px] object-cover"
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-gray-900/30 to-transparent"></div>

                {/* Introductory Text Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12">
                  <div className="max-w-3xl">
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                      Understanding Water-Climate Interactions for a Sustainable Future
                    </h3>
                    <p className="text-base sm:text-lg text-gray-200 leading-relaxed">
                      Advancing scientific knowledge through innovative research in hydroclimate systems, climate modeling, and water resource management.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Cards Below Image - Not Overlapping */}
            <div className="mt-8 max-w-5xl mx-auto px-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all">
                  <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-1">50+</div>
                  <div className="text-sm text-gray-600 font-medium">Publications</div>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all">
                  <div className="text-3xl font-bold bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent mb-1">1000+</div>
                  <div className="text-sm text-gray-600 font-medium">Citations</div>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all">
                  <div className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-green-600 bg-clip-text text-transparent mb-1">15+</div>
                  <div className="text-sm text-gray-600 font-medium">Projects</div>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all">
                  <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-1">10+</div>
                  <div className="text-sm text-gray-600 font-medium">Team</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Research Focus Section */}
      <div className="py-32 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Our Research Focus
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Exploring complex hydroclimate interactions through cutting-edge technology and interdisciplinary collaboration
            </p>
          </div>

          {/* Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden">
              {/* Gradient Background on Hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-cyan-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

              <div className="relative">
                {/* Icon */}
                <div className="w-20 h-20 mb-6 group-hover:scale-110 transition-transform duration-300">
                  <img
                    src="https://api.iconify.design/mdi:flask-outline.svg?color=%233b82f6&width=80&height=80"
                    alt="Advanced Modeling"
                    className="w-full h-full"
                  />
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Advanced Modeling
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Developing integrated hydroclimate frameworks with high-resolution projections and uncertainty quantification.
                </p>
              </div>
            </div>

            {/* Card 2 */}
            <div className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-50 to-teal-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

              <div className="relative">
                <div className="w-20 h-20 mb-6 group-hover:scale-110 transition-transform duration-300">
                  <img
                    src="https://api.iconify.design/mdi:chart-timeline-variant.svg?color=%2306b6d4&width=80&height=80"
                    alt="Extreme Events"
                    className="w-full h-full"
                  />
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Extreme Events
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Analyzing and projecting extreme hydroclimatic events using advanced statistical techniques and AI methods.
                </p>
              </div>
            </div>

            {/* Card 3 */}
            <div className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-50 to-green-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

              <div className="relative">
                <div className="w-20 h-20 mb-6 group-hover:scale-110 transition-transform duration-300">
                  <img
                    src="https://api.iconify.design/mdi:earth.svg?color=%2314b8a6&width=80&height=80"
                    alt="Water Resilience"
                    className="w-full h-full"
                  />
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Water Resilience
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Creating adaptive solutions for water resource management and climate resilience strategies worldwide.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="py-20 bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Join Us in Advancing Climate Science
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Collaborate with our team to develop innovative solutions for global hydroclimate challenges
          </p>
          <button
            onClick={() => onNavigate('contact')}
            className="px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105">
            Get In Touch
          </button>
        </div>
      </div>
    </div>
  );
}

// ResourcesPage Component
function ResourcesPage() {
  const repositories = [
    {
      title: "SCETD Approach for Temperature Extremes",
      description: "Advanced downscaling method assessing future changes in temperature extremes over the Yangtze River basin.",
      link: "#",
      icon: "fa-database",
      color: "from-blue-500 to-cyan-500",
      bgColor: "from-blue-50 to-cyan-50"
    },
    {
      title: "Runoff Variability Analysis",
      description: "Spatiotemporal regression analysis exploring vegetation dynamics and runoff variability in the Weihe River Basin.",
      link: "#",
      icon: "fa-chart-line",
      color: "from-cyan-500 to-teal-500",
      bgColor: "from-cyan-50 to-teal-50"
    },
    {
      title: "Climate Data Portal",
      description: "Interactive climate data portal for accessing high-resolution climate projections and downscaling models.",
      link: "/climate-portal",
      icon: "fa-cloud",
      color: "from-teal-500 to-green-500",
      bgColor: "from-teal-50 to-green-50"
    },
    {
      title: "Wastewater Optimizer",
      description: "Realize the application demonstration of key technologies for multi-energy flow collaborative planning of sewage treatment energy supply systems taking into account power grid auxiliary services.",
      link: "/wastewater-optimizer",
      icon: "fa-cogs",
      color: "from-green-500 to-emerald-500",
      bgColor: "from-green-50 to-emerald-50",
      external: true
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-cyan-50 to-slate-50">
        {/* Animated Background Blobs */}
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl font-extrabold mb-6">
              <span className="block bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-500 bg-clip-text text-transparent">
                Resources
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Access our data repositories, research tools, and interactive platforms supporting hydroclimate science
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Introduction Section */}
        <div className="mb-16 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-3xl p-8 border border-gray-200">
              <div className="flex items-center justify-center mb-6">
                <div className="w-12 h-12">
                  <img
                    src="https://api.iconify.design/mdi:archive.svg?color=%233b82f6&width=48&height=48"
                    alt="Archive"
                    className="w-full h-full"
                  />
                </div>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                The Hydroclimate Systems Lab maintains a comprehensive repository of research data, analytical tools,
                and interactive platforms. Our resources are regularly updated to support cutting-edge research in
                climate science and water resource management.
              </p>
            </div>
          </div>
        </div>

        {/* Repository Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {repositories.map((repo, index) => (
            <div
              key={index}
              className="group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100"
            >
              {/* Icon and Title Header */}
              <div className={`relative overflow-hidden bg-gradient-to-br ${repo.bgColor} p-8`}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <img
                      src={`https://api.iconify.design/mdi:${
                        repo.icon === 'fa-database' ? 'database' :
                        repo.icon === 'fa-chart-line' ? 'chart-line' :
                        repo.icon === 'fa-cloud' ? 'weather-cloudy' :
                        repo.icon === 'fa-cogs' ? 'cog-outline' : 'database'
                      }.svg?color=${
                        repo.color.includes('blue') ? '%233b82f6' :
                        repo.color.includes('cyan') ? '%2306b6d4' :
                        repo.color.includes('teal') ? '%2314b8a6' :
                        repo.color.includes('green') ? '%2310b981' : '%233b82f6'
                      }&width=48&height=48`}
                      alt={repo.title}
                      className="w-full h-full"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors flex-1">
                    {repo.title}
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                <p className="text-gray-600 leading-relaxed mb-6">
                  {repo.description}
                </p>
                <a
                  href={repo.link}
                  target={repo.external ? "_blank" : "_self"}
                  rel={repo.external ? "noopener noreferrer" : ""}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  {repo.title.includes("Climate") ? (
                    <>
                      <img src="https://api.iconify.design/mdi:open-in-new.svg?color=%23ffffff&width=16&height=16" alt="" className="w-4 h-4 mr-2" />
                      View Climate Portal
                    </>
                  ) : repo.title.includes("Wastewater") ? (
                    <>
                      <img src="https://api.iconify.design/mdi:rocket-launch.svg?color=%23ffffff&width=16&height=16" alt="" className="w-4 h-4 mr-2" />
                      View Software
                    </>
                  ) : (
                    <>
                      <img src="https://api.iconify.design/mdi:database.svg?color=%23ffffff&width=16&height=16" alt="" className="w-4 h-4 mr-2" />
                      View Data
                    </>
                  )}
                  <img src="https://api.iconify.design/mdi:arrow-right.svg?color=%23ffffff&width=16&height=16" alt="" className="w-4 h-4 ml-2" />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Resources Section */}
        <div className="bg-gradient-to-br from-blue-600 via-cyan-600 to-teal-600 rounded-3xl p-12 text-center shadow-2xl">
          <div className="max-w-3xl mx-auto">
            <div className="w-12 h-12 mx-auto mb-6">
              <img
                src="https://api.iconify.design/mdi:help-circle.svg?color=%23ffffff&width=48&height=48"
                alt="Help"
                className="w-full h-full"
              />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Need More Information?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              If you're looking for specific datasets or have questions about our research tools, please don't hesitate to reach out.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
              >
                <img src="https://api.iconify.design/mdi:email.svg?color=%232563eb&width=20&height=20" alt="" className="w-5 h-5 mr-2" />
                Contact Us
              </a>
              <a
                href="https://orcid.org/0000-0003-0098-1008"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white border-2 border-white rounded-xl font-semibold hover:bg-white/20 transition-all duration-300"
              >
                <img src="https://api.iconify.design/mdi:card-account-details.svg?color=%23ffffff&width=24&height=24" alt="" className="w-6 h-6 mr-2" />
                View Publications
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// AboutUsPage Component
function AboutUsPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-cyan-50 to-slate-50">
        {/* Animated Background Blobs */}
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl font-extrabold mb-6">
              <span className="block bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-500 bg-clip-text text-transparent">
                About Us
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Advancing the understanding of hydroclimatic processes for a sustainable future
            </p>
          </div>
        </div>
      </div>

      {/* Mission Section with Image */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Image Side */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-3xl blur-2xl opacity-20"></div>
            <img
              src="/images/aboutusbc.png"
              className="relative rounded-3xl shadow-2xl w-full"
              alt="About Us"
            />
          </div>

          {/* Content Side */}
          <div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Mission
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Hydroclimate Systems Lab is dedicated to advancing the understanding of hydroclimatic
              processes and their impacts on water resources, ecosystems, and society.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Our research combines cutting-edge modeling techniques with observational data to address critical
              challenges in water management and climate adaptation.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              We strive to provide science-based solutions for sustainable water resources management in a changing climate.
            </p>
          </div>
        </div>
      </div>

      {/* Core Values Section */}
      <div className="bg-gradient-to-br from-gray-50 to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What We Stand For
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our core values guide our research and collaboration
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Value Card 1 */}
            <div className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-cyan-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

              <div className="relative">
                <div className="w-20 h-20 mb-6 group-hover:scale-110 transition-transform duration-300">
                  <img
                    src="https://api.iconify.design/mdi:lightbulb-on.svg?color=%233b82f6&width=80&height=80"
                    alt="Innovation"
                    className="w-full h-full"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Innovation
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Developing novel methodologies and advanced modeling frameworks to push the boundaries of hydroclimate science.
                </p>
              </div>
            </div>

            {/* Value Card 2 */}
            <div className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-50 to-teal-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

              <div className="relative">
                <div className="w-20 h-20 mb-6 group-hover:scale-110 transition-transform duration-300">
                  <img
                    src="https://api.iconify.design/mdi:account-group.svg?color=%2306b6d4&width=80&height=80"
                    alt="Collaboration"
                    className="w-full h-full"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Collaboration
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Working together with interdisciplinary teams and international partners to tackle complex environmental challenges.
                </p>
              </div>
            </div>

            {/* Value Card 3 */}
            <div className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-50 to-green-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

              <div className="relative">
                <div className="w-20 h-20 mb-6 group-hover:scale-110 transition-transform duration-300">
                  <img
                    src="https://api.iconify.design/mdi:leaf-circle.svg?color=%2310b981&width=80&height=80"
                    alt="Sustainability"
                    className="w-full h-full"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Sustainability
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Providing science-based solutions that support sustainable water management and climate adaptation strategies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Research Focus Areas */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Research Focus
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our interdisciplinary approach addresses key challenges in hydroclimate science
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Focus Area 1 */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-8 border border-blue-100 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16">
                    <img
                      src="https://api.iconify.design/mdi:weather-pouring.svg?color=%233b82f6&width=64&height=64"
                      alt="Climate Extremes"
                      className="w-full h-full"
                    />
                  </div>
                </div>
                <div className="ml-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Climate Extremes Analysis
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Investigating patterns, drivers, and future projections of extreme precipitation, drought, and temperature events under climate change.
                  </p>
                </div>
              </div>
            </div>

            {/* Focus Area 2 */}
            <div className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-3xl p-8 border border-cyan-100 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16">
                    <img
                      src="https://api.iconify.design/mdi:water.svg?color=%2306b6d4&width=64&height=64"
                      alt="Water Resources"
                      className="w-full h-full"
                    />
                  </div>
                </div>
                <div className="ml-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Water Resources Management
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Developing integrated models for sustainable water allocation, flood risk assessment, and reservoir operations.
                  </p>
                </div>
              </div>
            </div>

            {/* Focus Area 3 */}
            <div className="bg-gradient-to-br from-teal-50 to-green-50 rounded-3xl p-8 border border-teal-100 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16">
                    <img
                      src="https://api.iconify.design/mdi:chart-line.svg?color=%2314b8a6&width=64&height=64"
                      alt="Statistical Downscaling"
                      className="w-full h-full"
                    />
                  </div>
                </div>
                <div className="ml-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Statistical Downscaling
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Creating advanced statistical methods to translate global climate model outputs into regional and local-scale projections.
                  </p>
                </div>
              </div>
            </div>

            {/* Focus Area 4 */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-8 border border-green-100 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16">
                    <img
                      src="https://api.iconify.design/mdi:sprout.svg?color=%2310b981&width=64&height=64"
                      alt="Ecosystem Response"
                      className="w-full h-full"
                    />
                  </div>
                </div>
                <div className="ml-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Ecosystem Response
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Analyzing vegetation dynamics, drought resistance, and ecosystem resilience in response to hydroclimatic variability.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// PeoplePage Component
function PeoplePage({ onNavigate }) {
  const staff = [
    {
      name: "Dr. Xiong Zhou",
      image: "/images/Xiong Zhou.jpg",
      title: "Assistant Professor",
      institution: "Beijing Normal University",
      research: "Climate Change, Hydroclimate Systems",
      email: "zhou.xiong@outlook.com",
      link: "/cv"
    }
  ];

  const graduateStudents = [
    {
      name: "Shuxin Wang",
      image: "/images/shuxin.jpg",
      title: "Master's Student (Starting Fall 2025)",
      institution: "Beijing Normal University",
      research: "Climate Change, Hydroclimate Systems",
      email: "202111180047@mail.bnu.edu.cn",
      link: "#",
      note: "B.S. June 2025"
    }
  ];

  const undergraduateStudents = [
    {
      name: "Ruidi Sun",
      image: "https://api.iconify.design/mdi:account-circle.svg?color=%233b82f6&width=400&height=400",
      title: "B.S. Environmental Science (Expected June 2026)",
      institution: "Beijing Normal University",
      research: "Climate Change, Hydroclimate Systems",
      email: null,
      link: "#",
      thesis: "Driving Factors of Terrestrial Water Storage Variations on the Tibetan Plateau under a Changing Climate",
      isVectorAvatar: true
    },
    {
      name: "Sihan Zhu",
      image: "https://api.iconify.design/mdi:account-circle.svg?color=%233b82f6&width=400&height=400",
      title: "B.S. Environmental Science (Expected June 2026)",
      institution: "Beijing Normal University",
      research: "Climate Change, Hydroclimate Systems",
      email: null,
      link: "#",
      thesis: "Impacts of Drought on Hydrological Changes and Watershed Response in the Loess Plateau under Climate Change",
      isVectorAvatar: true
    }
  ];

  const alumni = [
    {
      name: "Xuan Huang",
      image: "https://api.iconify.design/mdi:account-circle.svg?color=%233b82f6&width=400&height=400",
      title: "B.S. Environmental Science, June 2022",
      institution: "Beijing Normal University",
      research: "Climate Change and Temperature Extremes",
      email: null,
      link: "#",
      thesis: "Temperature Change Projection and Extreme Value Analysis under Multi-Greenhouse Gas Emission Scenarios in Central China",
      isVectorAvatar: true
    },
    {
      name: "Shuxin Wang",
      image: "/images/shuxin.jpg",
      title: "B.S. Environmental Science, June 2025",
      institution: "Beijing Normal University",
      research: "Climate Change, Hydroclimate Systems",
      email: "202111180047@mail.bnu.edu.cn",
      link: "#",
      thesis: "Impact of International Trade on China's Water Resource Stress under Climate Change Conditions"
    },
    {
      name: "Baoyi Huang",
      image: "/images/baoyi.jpeg",
      title: "B.S. Environmental Science, June 2025",
      institution: "Beijing Normal University",
      research: "Climate Change, Hydroclimate Systems",
      email: "109556051@qq.com",
      link: "#",
      thesis: "Impact of Drought on Terrestrial Water Storage in the Qinghai-Tibet Plateau under Climate Change Conditions"
    }
  ];

  const TeamMemberCard = ({ member, onLinkClick }) => (
    <div className="group">
      <div className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 h-full flex flex-col">
        {/* Image Container */}
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-cyan-50 p-8">
          {member.link === '/cv' ? (
            <button onClick={() => onLinkClick('cv')} className="w-full">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full blur-2xl opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
                <img
                  src={member.image}
                  alt={member.name}
                  className="relative w-40 h-40 mx-auto rounded-full object-cover border-4 border-white shadow-xl group-hover:scale-105 transition-transform duration-300"
                  style={{objectPosition: 'center 15%'}}
                />
              </div>
            </button>
          ) : (
            <a href={member.link} className="block">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full blur-2xl opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
                <img
                  src={member.image}
                  alt={member.name}
                  className="relative w-40 h-40 mx-auto rounded-full object-cover border-4 border-white shadow-xl group-hover:scale-105 transition-transform duration-300"
                  style={{objectPosition: 'center 15%'}}
                />
              </div>
            </a>
          )}
        </div>

        {/* Info Container */}
        <div className="p-6 flex-grow flex flex-col">
          <h3 className="text-xl font-bold text-gray-900 text-center mb-2 group-hover:text-blue-600 transition-colors">
            {member.name}
          </h3>
          <p className="text-sm text-gray-500 text-center mb-1">{member.title}</p>
          {member.note && (
            <p className="text-xs text-blue-600 text-center mb-1 font-medium">{member.note}</p>
          )}
          <p className="text-sm text-gray-500 text-center mb-4">{member.institution}</p>

          <div className="mt-auto">
            <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-4 mb-3">
              <p className="text-xs font-semibold text-gray-700 mb-1">Research Interests</p>
              <p className="text-sm text-gray-600">{member.research}</p>
            </div>

            {member.thesis && (
              <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl p-4 mb-3">
                <p className="text-xs font-semibold text-gray-700 mb-1">Thesis</p>
                <p className="text-sm text-gray-600 italic">{member.thesis}</p>
              </div>
            )}

            {member.email && (
              <a
                href={`mailto:${member.email}`}
                className="flex items-center justify-center text-sm text-blue-600 hover:text-blue-700 font-medium group-hover:underline transition"
              >
                <i className="fas fa-envelope mr-2"></i>
                {member.email}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-cyan-50 to-slate-50">
        {/* Animated Background Blobs */}
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl font-extrabold mb-6">
              <span className="block bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-500 bg-clip-text text-transparent">
                Our Team
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the talented researchers and students advancing hydroclimate science
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Principal Investigator Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Principal Investigator
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-600 mx-auto rounded-full"></div>
          </div>
          <div className="max-w-2xl mx-auto">
            {staff.map((member, index) => (
              <TeamMemberCard key={index} member={member} onLinkClick={onNavigate} />
            ))}
          </div>
        </div>

        {/* Graduate Students Section */}
        {graduateStudents.length > 0 && (
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Graduate Students
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-600 mx-auto rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {graduateStudents.map((member, index) => (
                <TeamMemberCard key={index} member={member} onLinkClick={onNavigate} />
              ))}
            </div>
          </div>
        )}

        {/* Undergraduate Students Section */}
        {undergraduateStudents.length > 0 && (
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Undergraduate Students
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-cyan-600 to-teal-600 mx-auto rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {undergraduateStudents.map((member, index) => (
                <TeamMemberCard key={index} member={member} onLinkClick={onNavigate} />
              ))}
            </div>
          </div>
        )}

        {/* Alumni Section */}
        {alumni.length > 0 && (
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Alumni
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-teal-600 to-green-600 mx-auto rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {alumni.map((member, index) => (
                <TeamMemberCard key={index} member={member} onLinkClick={onNavigate} />
              ))}
            </div>
          </div>
        )}

        {/* Join Our Team Section */}
        <div className="bg-gradient-to-br from-blue-600 via-cyan-600 to-teal-600 rounded-3xl p-12 text-center shadow-2xl">
          <div className="max-w-3xl mx-auto">
            <div className="w-20 h-20 mx-auto mb-6">
              <img
                src="https://api.iconify.design/mdi:account-multiple-plus.svg?color=%23ffffff&width=80&height=80"
                alt="Join Our Team"
                className="w-full h-full"
              />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Join Our Team
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              We are always looking for motivated graduate students and postdocs passionate about hydroclimate research!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => onNavigate('contact')}
                className="px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
              >
                Get In Touch
              </button>
              <a
                href="mailto:zhou.xiong@outlook.com"
                className="px-8 py-4 bg-white/10 text-white border-2 border-white rounded-xl font-semibold hover:bg-white/20 transition-all duration-300"
              >
                Send Email
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// CVPage Component
function CVPage({ onNavigate }) {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-cyan-50 to-slate-50">
        {/* Animated Background Blobs */}
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl font-extrabold mb-6">
              <span className="block bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-500 bg-clip-text text-transparent">
                Curriculum Vitae
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Dr. Xiong Zhou - Assistant Professor
            </p>
          </div>
        </div>
      </div>

      {/* Profile Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 mb-16">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8">
            {/* Profile Photo */}
            <div className="lg:col-span-1 flex justify-center items-start">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full blur-2xl opacity-30"></div>
                <img
                  src="/images/Xiong Zhou2.jpg"
                  alt="Dr. Xiong Zhou"
                  className="relative w-64 h-64 rounded-full object-cover border-4 border-white shadow-xl"
                />
              </div>
            </div>

            {/* Quick Info */}
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Dr. Xiong Zhou</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 mr-3">
                    <img
                      src="https://api.iconify.design/mdi:school.svg?color=%233b82f6&width=40&height=40"
                      alt="Affiliation"
                      className="w-full h-full"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Affiliation</p>
                    <p className="text-gray-600">Beijing Normal University</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 mr-3">
                    <img
                      src="https://api.iconify.design/mdi:email.svg?color=%2306b6d4&width=40&height=40"
                      alt="Email"
                      className="w-full h-full"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Email</p>
                    <a href="mailto:zhou.xiong@outlook.com" className="text-blue-600 hover:underline">zhou.xiong@outlook.com</a>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 mr-3">
                    <img
                      src="https://api.iconify.design/mdi:card-account-details.svg?color=%2314b8a6&width=40&height=40"
                      alt="ORCID"
                      className="w-full h-full"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700">ORCID</p>
                    <a href="https://orcid.org/0000-0003-0098-1008" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-sm">0000-0003-0098-1008</a>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 mr-3">
                    <img
                      src="https://api.iconify.design/mdi:flask.svg?color=%2310b981&width=40&height=40"
                      alt="Research"
                      className="w-full h-full"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Research</p>
                    <p className="text-gray-600">Climate Change, Hydroclimate Systems</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CV Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {/* Citation Metrics - Highlight Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-8 border border-blue-100 text-center">
            <div className="text-4xl font-bold text-blue-600 mb-2">1098</div>
            <p className="text-gray-700 font-semibold mb-1">Total Citations</p>
            <p className="text-sm text-gray-500">(952 since 2020)</p>
          </div>
          <div className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-3xl p-8 border border-cyan-100 text-center">
            <div className="text-4xl font-bold text-cyan-600 mb-2">18</div>
            <p className="text-gray-700 font-semibold mb-1">h-index</p>
            <p className="text-sm text-gray-500">(17 since 2020)</p>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-green-50 rounded-3xl p-8 border border-green-100 text-center">
            <div className="text-4xl font-bold text-teal-600 mb-2">38</div>
            <p className="text-gray-700 font-semibold mb-1">i10-index</p>
            <p className="text-gray-500 text-sm">(36 since 2020)</p>
          </div>
        </div>

        {/* Education */}
        <section className="mb-16">
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 mr-4">
              <img
                src="https://api.iconify.design/mdi:school.svg?color=%233b82f6&width=48&height=48"
                alt="Education"
                className="w-full h-full"
              />
            </div>
            <h3 className="text-3xl font-bold text-gray-900">Education</h3>
          </div>
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 border-l-4 border-blue-500 shadow-md hover:shadow-lg transition">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Doctor of Philosophy</h4>
                  <p className="text-gray-700 mb-2">Environmental Systems Engineering</p>
                  <p className="text-gray-600">University of Regina, Regina, Canada</p>
                </div>
                <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">2015 - 2020</span>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 border-l-4 border-cyan-500 shadow-md hover:shadow-lg transition">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Master of Applied Science</h4>
                  <p className="text-gray-700 mb-2">Environmental Systems Engineering</p>
                  <p className="text-gray-600">University of Regina, Regina, Canada</p>
                </div>
                <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">2012 - 2014</span>
              </div>
            </div>
          </div>
        </section>

        {/* Employment */}
        <section className="mb-16">
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 mr-4">
              <img
                src="https://api.iconify.design/mdi:briefcase.svg?color=%2306b6d4&width=48&height=48"
                alt="Employment"
                className="w-full h-full"
              />
            </div>
            <h3 className="text-3xl font-bold text-gray-900">Employment</h3>
          </div>
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-100">
              <div className="flex items-start justify-between mb-3">
                <h4 className="text-xl font-bold text-gray-900">Assistant Professor</h4>
                <span className="text-sm text-green-700 bg-green-100 px-3 py-1 rounded-full font-medium">Current</span>
              </div>
              <p className="text-gray-700 mb-1">School of Environment</p>
              <p className="text-gray-600 mb-2">Beijing Normal University, Beijing, China</p>
              <p className="text-sm text-gray-500">2015 - Present</p>
            </div>
            <div className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-2xl p-6 border border-cyan-100">
              <div className="flex items-start justify-between mb-3">
                <h4 className="text-xl font-bold text-gray-900">Research Scientist</h4>
                <span className="text-sm text-green-700 bg-green-100 px-3 py-1 rounded-full font-medium">Current</span>
              </div>
              <p className="text-gray-700 mb-1">Institute for Energy Environment and Sustainable Communities</p>
              <p className="text-gray-600 mb-2">University of Regina, Regina, Canada</p>
              <p className="text-sm text-gray-500">2020 - Present</p>
            </div>
          </div>
        </section>

        {/* Research Interests */}
        <section className="mb-16">
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 mr-4">
              <img
                src="https://api.iconify.design/mdi:flask.svg?color=%2314b8a6&width=48&height=48"
                alt="Research"
                className="w-full h-full"
              />
            </div>
            <h3 className="text-3xl font-bold text-gray-900">Research Interests & Methods</h3>
          </div>
          <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 border border-gray-200">
            <p className="text-gray-700 leading-relaxed text-lg">
              Dr. Xiong Zhou's primary research focus is on <span className="font-semibold text-blue-600">climate change</span>. His work is characterized by the application of
              advanced quantitative methods—including <span className="font-semibold">statistical downscaling</span>, <span className="font-semibold">dynamical modeling</span>, and <span className="font-semibold">stochastic
              programming</span>—to understand and project the impacts of climate change. His research encompasses critical areas
              such as changes in temperature and precipitation extremes, hydrological impacts, and the development of
              sustainable energy and water management strategies under future climate scenarios.
            </p>
          </div>
        </section>

        {/* Professional Memberships */}
        <section className="mb-16">
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 mr-4">
              <img
                src="https://api.iconify.design/mdi:account-group.svg?color=%239333ea&width=48&height=48"
                alt="Memberships"
                className="w-full h-full"
              />
            </div>
            <h3 className="text-3xl font-bold text-gray-900">Professional Memberships</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition">
              <div className="w-10 h-10 mb-3">
                <img
                  src="https://api.iconify.design/mdi:earth.svg?color=%233b82f6&width=40&height=40"
                  alt="AGU"
                  className="w-full h-full"
                />
              </div>
              <p className="text-gray-800 font-medium">American Geophysical Union (AGU)</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-cyan-300 hover:shadow-lg transition">
              <div className="w-10 h-10 mb-3">
                <img
                  src="https://api.iconify.design/mdi:leaf.svg?color=%2306b6d4&width=40&height=40"
                  alt="IEESC"
                  className="w-full h-full"
                />
              </div>
              <p className="text-gray-800 font-medium">International Society of Environmental Information Sciences (IEESC)</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-teal-300 hover:shadow-lg transition">
              <div className="w-10 h-10 mb-3">
                <img
                  src="https://api.iconify.design/mdi:cog.svg?color=%2314b8a6&width=40&height=40"
                  alt="APEGS"
                  className="w-full h-full"
                />
              </div>
              <p className="text-gray-800 font-medium">Association of Professional Engineers and Geoscientists of Saskatchewan (APEGS)</p>
            </div>
          </div>
        </section>

        {/* Awards and Honors */}
        <section className="mb-16">
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 mr-4">
              <img
                src="https://api.iconify.design/mdi:trophy.svg?color=%23eab308&width=48&height=48"
                alt="Awards"
                className="w-full h-full"
              />
            </div>
            <h3 className="text-3xl font-bold text-gray-900">Awards and Honors</h3>
          </div>
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-md">
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <i className="fas fa-award text-yellow-500 mr-3 mt-1"></i>
                <span>Chinese Government Award for Outstanding Self-financed Students Abroad, China Scholarship Council, 2019</span>
              </li>
              <li className="flex items-start">
                <i className="fas fa-award text-yellow-500 mr-3 mt-1"></i>
                <span>Ph.D. Award, University of Regina, 2019 – 2020</span>
              </li>
              <li className="flex items-start">
                <i className="fas fa-award text-yellow-500 mr-3 mt-1"></i>
                <span>UR Graduate Scholarship, University of Regina, 2018</span>
              </li>
              <li className="flex items-start">
                <i className="fas fa-award text-yellow-500 mr-3 mt-1"></i>
                <span>Graduate Teaching Assistantship, University of Regina, 2016 – 2017</span>
              </li>
              <li className="flex items-start">
                <i className="fas fa-award text-yellow-500 mr-3 mt-1"></i>
                <span>Graduate Research Fellowship, University of Regina, 2015 – 2018</span>
              </li>
              <li className="flex items-start">
                <i className="fas fa-award text-yellow-500 mr-3 mt-1"></i>
                <span>Graduate Research Award, University of Regina, 2013</span>
              </li>
              <li className="flex items-start">
                <i className="fas fa-award text-yellow-500 mr-3 mt-1"></i>
                <span>Shen Kuo Travel Bursary, University of Regina, 2013</span>
              </li>
              <li className="flex items-start">
                <i className="fas fa-award text-yellow-500 mr-3 mt-1"></i>
                <span>Outstanding Thesis Award, Xiamen University of Technology, 2011</span>
              </li>
              <li className="flex items-start">
                <i className="fas fa-award text-yellow-500 mr-3 mt-1"></i>
                <span>China National Scholarship, Ministry of Education, 2009</span>
              </li>
              <li className="flex items-start">
                <i className="fas fa-award text-yellow-500 mr-3 mt-1"></i>
                <span>Provincial Merit Student, Education Department of Fujian Province, 2009</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Teaching Experience */}
        <section className="mb-16">
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 mr-4">
              <img
                src="https://api.iconify.design/mdi:teach.svg?color=%236366f1&width=48&height=48"
                alt="Teaching"
                className="w-full h-full"
              />
            </div>
            <h3 className="text-3xl font-bold text-gray-900">Teaching Experience</h3>
          </div>
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border border-indigo-100">
            <div className="space-y-4 text-gray-700">
              <div className="border-l-4 border-indigo-500 pl-4 py-2">
                <p className="font-bold text-gray-900">ENEV 801, Environmental Systems Engineering</p>
                <p className="text-sm">Guest Lecturer (Practical Applications), 2017</p>
              </div>
              <div className="border-l-4 border-purple-500 pl-4 py-2">
                <p className="font-bold text-gray-900">ENEV 862, Environmental Modeling and Decision-Making</p>
                <p className="text-sm">Guest Lecturer (Multiobjective Optimization: Fractional Programming), 2017</p>
              </div>
              <div className="border-l-4 border-indigo-500 pl-4 py-2">
                <p className="font-bold text-gray-900">ENGG401, Engineering Law and Professionalism</p>
                <p className="text-sm">Teaching Assistant, 2017</p>
              </div>
              <div className="border-l-4 border-purple-500 pl-4 py-2">
                <p className="font-bold text-gray-900">ENEV281, Surveying, Mapping and Information Systems</p>
                <p className="text-sm">Teaching Assistant & Lab Instructor, 2016 - 2017</p>
              </div>
            </div>
          </div>
        </section>

        {/* Manuscript Review */}
        <section className="mb-16">
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 mr-4">
              <img
                src="https://api.iconify.design/mdi:file-document-edit.svg?color=%2310b981&width=48&height=48"
                alt="Manuscript Review"
                className="w-full h-full"
              />
            </div>
            <h3 className="text-3xl font-bold text-gray-900">Manuscript Review</h3>
          </div>
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                "Advances in Water Resources",
                "Applied Energy",
                "Renewable and Sustainable Energy Reviews",
                "Atmospheric Environment",
                "Climatic Change",
                "European Journal of Operational Research",
                "Journal of Applied Meteorology and Climatology",
                "Journal of Cleaner Production",
                "Journal of Environmental Engineering",
                "Journal of Environmental Informatics",
                "Journal of Environmental Management",
                "Journal of Hydroinformatics",
                "Journal of Hydrology",
                "Journal of Water and Climate Change",
                "Journal of Water Resources Planning and Management",
                "National Science Review",
                "Resources, Conservation and Recycling",
                "Science of the Total Environment",
                "Stochastic Environmental Research and Risk Assessment",
                "Waste Management",
                "Water Research",
                "Water Resources Management"
              ].map((journal, index) => (
                <div key={index} className="flex items-center text-sm text-gray-700 bg-gray-50 rounded-lg p-3 hover:bg-blue-50 transition">
                  <i className="fas fa-check-circle text-green-500 mr-2 flex-shrink-0"></i>
                  <span>{journal}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Presentations */}
        <section className="mb-16">
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 mr-4">
              <img
                src="https://api.iconify.design/mdi:presentation.svg?color=%23ef4444&width=48&height=48"
                alt="Presentations"
                className="w-full h-full"
              />
            </div>
            <h3 className="text-3xl font-bold text-gray-900">Selected Presentations</h3>
          </div>
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition">
              <p className="text-gray-800 mb-2"><span className="font-bold text-blue-600">AGU Fall Meeting 2019</span> - San Francisco, USA</p>
              <p className="text-sm text-gray-600">Long-term hydrological impacts of RCMs-projected climate changes on Canadian Prairies</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition">
              <p className="text-gray-800 mb-2"><span className="font-bold text-blue-600">AGU Fall Meeting 2019</span> - San Francisco, USA</p>
              <p className="text-sm text-gray-600">High-resolution hydrological projection and long-term flood mapping for Canadian Prairies</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition">
              <p className="text-gray-800 mb-2"><span className="font-bold text-blue-600">Climate Change Research Workshop 2018</span> - Regina, Canada</p>
              <p className="text-sm text-gray-600">High-resolution probabilistic climate projections</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition">
              <p className="text-gray-800 mb-2"><span className="font-bold text-blue-600">Environmental Simulation Workshop 2018</span> - Banff, Canada</p>
              <p className="text-sm text-gray-600">PRECIS-projected increases in temperature and precipitation over Canada</p>
            </div>
          </div>
        </section>

        {/* Back Button */}
        <div className="text-center mt-16 mb-8">
          <button
            onClick={() => onNavigate('people')}
            className="px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to People
          </button>
        </div>
      </div>
    </div>
  );
}

// ResearchPage Component
function ResearchPage() {
  const researchAreas = [
    {
      title: "Integrated Hydroclimate Modeling and Downscaling",
      image: "/images/subarea1.png",
      description: "Developing coupled frameworks that merge regional climate models with hydrological simulation tools to generate high-resolution projections.",
      anchor: "subarea-1"
    },
    {
      title: "Analysis of Extreme Hydroclimatic Events",
      image: "/images/subarea2.jpg",
      description: "Employing statistical techniques to identify and project extreme events like heatwaves, droughts, and compound climate conditions.",
      anchor: "subarea-2"
    },
    {
      title: "Water Resources and Runoff Dynamics",
      image: "/images/subarea3.jpeg",
      description: "Evaluating water sustainability through analysis of stress indices and runoff dynamics across major river basins.",
      anchor: "subarea-3"
    },
    {
      title: "Innovative Solutions for Hydroclimate Resilience",
      image: "/images/subarea4.png",
      description: "Translating scientific findings into practical applications through decision support tools and adaptive management frameworks.",
      anchor: "subarea-4"
    }
  ];

  const detailedSubareas = [
    {
      id: "subarea-1",
      title: "1. Integrated Hydroclimate Modeling and Downscaling",
      image: "/images/subarea1.png",
      description: "This subarea centers on the development of coupled dynamical–statistical frameworks that merge regional climate models with hydrological simulation tools. The approach integrates models such as PRECIS with advanced statistical techniques—including dynamical–copula methods and bias correction algorithms—to generate high-resolution time series of key hydroclimatic variables (e.g., precipitation, temperature, runoff). These integrated models are designed to resolve fine-scale spatial and temporal variability, enabling detailed analyses of water availability and flood risk in river basins. Such methodologies also facilitate the assessment of uncertainty by refining projections from global climate models, ultimately informing water resource management and infrastructure planning."
    },
    {
      id: "subarea-2",
      title: "2. Analysis of Extreme Hydroclimatic Events",
      image: "/images/subarea2.jpg",
      description: "This research domain involves the statistical identification and projection of extreme hydroclimatic events. Advanced techniques—such as spatiotemporal geographic weighted regression and stepwise regression—are employed to quantify the frequency, duration, and intensity of extremes like heatwaves, droughts, and compound events (e.g., concurrent high temperatures and anomalous precipitation conditions). In addition, the role of large-scale teleconnection patterns (for instance, ENSO and AMO) in modulating these extremes is thoroughly examined. By elucidating the underlying physical mechanisms, this subarea contributes to the development of robust risk assessment models and early warning systems that are crucial for mitigating impacts on agriculture, ecosystems, and urban infrastructure."
    },
    {
      id: "subarea-3",
      title: "3. Water Resources and Runoff Dynamics in River Basins",
      image: "/images/subarea3.jpeg",
      description: "Focused on evaluating water resource sustainability, this subarea examines water stress indices and runoff dynamics across major river basins. Comprehensive analyses incorporate both natural factors (e.g., surface runoff, snowmelt) and anthropogenic influences (e.g., water withdrawal for agriculture and industry). Spatially explicit modeling techniques are used to project future changes in water availability under multiple emission scenarios, revealing regional disparities in water stress. These studies assess how variations in vegetation cover and land-use changes interact with climate variability to modify runoff patterns, thereby providing critical insights for adaptive water management policies and for addressing potential risks of water scarcity."
    },
    {
      id: "subarea-4",
      title: "4. Innovative Solutions for Hydroclimate Resilience",
      image: "/images/subarea4.png",
      description: "Translating scientific findings into practical applications is a cornerstone of this research. This subarea focuses on developing decision support tools and adaptive management frameworks that incorporate real-time data assimilation from remote sensing and ground observations. Uncertainty quantification methods—such as mixed-level factorial analysis and Bayesian model averaging—are employed to identify dominant sources of variability in hydroclimatic projections. The resulting strategies include optimized water allocation systems, adaptive infrastructure design, and scenario-based planning that can be tailored to specific regions. These innovative solutions are designed to enhance resilience in water management systems, ensuring that policies and engineering practices are robust against future hydroclimatic changes."
    }
  ];

  const scrollToSection = (anchor) => {
    const element = document.getElementById(anchor);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-cyan-50 to-slate-50">
        {/* Animated Background Blobs */}
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl font-extrabold mb-6">
              <span className="block bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-500 bg-clip-text text-transparent">
                Research Areas
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              The hydroclimate systems laboratory focuses on advancing the understanding of complex interactions among
              atmospheric, hydrological, and land surface processes through innovative modeling frameworks and
              data-driven analyses.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Research Areas Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {researchAreas.map((area, index) => (
            <div
              key={index}
              className="group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100"
            >
              <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-cyan-50 p-8">
                <img
                  src={area.image}
                  alt={area.title}
                  className="mx-auto rounded-2xl shadow-lg group-hover:scale-105 transition-transform duration-300"
                  style={{maxHeight: '200px', objectFit: 'contain'}}
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors">
                  {area.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6">{area.description}</p>
                <button
                  onClick={() => scrollToSection(area.anchor)}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  Learn More
                  <i className="fas fa-arrow-right ml-2"></i>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Detailed Sub-Areas */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Research in Detail</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-600 mx-auto rounded-full"></div>
          </div>

          <div className="space-y-16">
            {detailedSubareas.map((subarea, index) => (
              <section key={index} id={subarea.id} className="scroll-mt-24">
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  {/* Image Side */}
                  <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-3xl blur-2xl opacity-20"></div>
                      <img
                        src={subarea.image}
                        alt={subarea.title}
                        className="relative w-full rounded-3xl shadow-2xl"
                      />
                    </div>
                  </div>

                  {/* Content Side */}
                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-3xl p-8 border border-gray-200">
                      <div className="flex items-center mb-6">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                          <span className="text-white text-2xl font-bold">{index + 1}</span>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">
                          {subarea.title.replace(/^\d+\.\s*/, '')}
                        </h3>
                      </div>
                      <p className="text-gray-700 leading-relaxed">
                        {subarea.description}
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// PublicationsPage Component
function PublicationsPage() {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const parseBibtex = (bibtexText) => {
      const entryRegex = /@(\w+)\s*{\s*([^,]+),([\s\S]*?)\n}/g;
      const fieldRegex = /(\w+)\s*=\s*[{"]([^}"]+)[}"]/g;
      let entries = [];
      let entryMatch;

      while ((entryMatch = entryRegex.exec(bibtexText)) !== null) {
        let type = entryMatch[1].trim();
        let citationKey = entryMatch[2].trim();
        let fieldsText = entryMatch[3].trim();
        let fields = {};
        let fieldMatch;

        while ((fieldMatch = fieldRegex.exec(fieldsText)) !== null) {
          let fieldName = fieldMatch[1].toLowerCase();
          let fieldValue = fieldMatch[2].trim();
          fields[fieldName] = fieldValue;
        }
        entries.push({ type, citationKey, fields });
      }
      return entries;
    };

    const loadPublications = async () => {
      try {
        const response = await fetch('/files/publications.bib');
        if (!response.ok) throw new Error('Failed to load publications');
        const bibtexText = await response.text();
        const entries = parseBibtex(bibtexText);

        // Sort by year (descending) then by title
        entries.sort((a, b) => {
          let yearA = parseInt(a.fields.year) || 0;
          let yearB = parseInt(b.fields.year) || 0;
          if (yearB !== yearA) return yearB - yearA;

          let titleA = a.fields.title ? a.fields.title.toLowerCase() : "";
          let titleB = b.fields.title ? b.fields.title.toLowerCase() : "";
          if (titleA < titleB) return -1;
          if (titleA > titleB) return 1;
          return 0;
        });

        setPublications(entries);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    loadPublications();
  }, []);

  const formatCitation = (entry) => {
    const author = entry.fields.author || "Unknown Author";
    const year = entry.fields.year || "N/A";
    const title = entry.fields.title || "Untitled";
    const journal = entry.fields.journal || "";
    const volume = entry.fields.volume || "";
    const number = entry.fields.number || "";
    const pages = entry.fields.pages || "";
    const doi = entry.fields.doi || "";

    // Format author names in APA style (Last, F. M.)
    let formattedAuthor = author;

    // APA format: Author, A. A. (Year). Title. Journal Name, volume(number), pages. https://doi.org/xxxxx
    let citation = `${formattedAuthor} (${year}). ${title}`;

    if (journal) {
      citation += `. <em>${journal}</em>`;
      if (volume) {
        citation += `, <em>${volume}</em>`;
        if (number) citation += `(${number})`;
      }
      if (pages) citation += `, ${pages}`;
    }

    if (doi) {
      citation += `. https://doi.org/${doi}`;
    } else {
      citation += ".";
    }

    // Highlight Zhou, X. or Zhou, Xiong
    const parts = citation.split(/(Zhou,\s*X(?:iong)?\.?)/gi);
    return parts.map((part, index) => {
      if (/Zhou,\s*X(?:iong)?\.?/i.test(part)) {
        return <strong key={index} className="text-blue-600">{part}</strong>;
      }
      // Handle italic tags for journal names
      if (part.includes('<em>')) {
        const italicParts = part.split(/(<em>|<\/em>)/);
        return italicParts.map((iPart, iIndex) => {
          if (iPart === '<em>') return null;
          if (iPart === '</em>') return null;
          const prevTag = italicParts[iIndex - 1];
          if (prevTag === '<em>') {
            return <em key={`${index}-${iIndex}`}>{iPart}</em>;
          }
          return <span key={`${index}-${iIndex}`}>{iPart}</span>;
        });
      }
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-cyan-50 to-slate-50">
        {/* Animated Background Blobs */}
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl font-extrabold mb-6">
              <span className="block bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-500 bg-clip-text text-transparent">
                Publications
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our research contributions to hydroclimate science and water resources management
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {loading && (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
            <p className="text-xl text-gray-600 mt-4">Loading publications...</p>
          </div>
        )}

        {error && (
          <div className="text-center py-20">
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-6 inline-block">
              <p className="text-xl text-red-600">
                <i className="fas fa-exclamation-circle mr-2"></i>
                Error: {error}
              </p>
            </div>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-8 border border-blue-100 text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{publications.length}</div>
                <p className="text-gray-700 font-semibold">Total Publications</p>
              </div>
              <div className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-3xl p-8 border border-cyan-100 text-center">
                <div className="text-4xl font-bold text-cyan-600 mb-2">1098</div>
                <p className="text-gray-700 font-semibold">Total Citations</p>
              </div>
              <div className="bg-gradient-to-br from-teal-50 to-green-50 rounded-3xl p-8 border border-green-100 text-center">
                <div className="text-4xl font-bold text-teal-600 mb-2">18</div>
                <p className="text-gray-700 font-semibold">h-index</p>
              </div>
            </div>

            {/* Publications List */}
            <div className="mb-16">
              <div className="flex items-center mb-8">
                <div className="w-12 h-12 mr-4">
                  <img
                    src="https://api.iconify.design/mdi:book-open-page-variant.svg?color=%233b82f6&width=48&height=48"
                    alt="Published Works"
                    className="w-full h-full"
                  />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Published Works</h2>
              </div>

              <div className="space-y-6">
                {publications.map((entry, index) => (
                  <div key={index} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-lg transition-shadow duration-300">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4 mt-1">
                        <span className="text-blue-600 font-bold">{index + 1}</span>
                      </div>
                      <div className="flex-1">
                        <div className="text-gray-800 leading-relaxed">
                          {formatCitation(entry)}
                        </div>
                        {entry.fields.doi && (
                          <div className="mt-3">
                            <a
                              href={`https://doi.org/${entry.fields.doi}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                              <i className="fas fa-external-link-alt mr-2"></i>
                              View Publication
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ORCID Link Section */}
            <div className="bg-gradient-to-br from-blue-600 via-cyan-600 to-teal-600 rounded-3xl p-12 text-center shadow-2xl">
              <div className="max-w-3xl mx-auto">
                <div className="w-16 h-16 mx-auto mb-6">
                  <img
                    src="https://api.iconify.design/mdi:card-account-details.svg?color=%23ffffff&width=64&height=64"
                    alt="ORCID"
                    className="w-full h-full"
                  />
                </div>
                <h2 className="text-3xl font-bold text-white mb-4">
                  Complete Publication List
                </h2>
                <p className="text-xl text-blue-100 mb-8">
                  For a comprehensive list of all publications, please visit our ORCID profile
                </p>
                <a
                  href="https://orcid.org/0000-0003-0098-1008"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
                >
                  <i className="fab fa-orcid mr-2 text-2xl"></i>
                  Visit ORCID Profile
                  <i className="fas fa-arrow-right ml-2"></i>
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// NewsPage Component
function NewsPage() {
  const newsItems = [
    {
      title: "New Publication in Journal of Cleaner Production",
      date: "2024-03",
      category: "Publication",
      journal: "Journal of Cleaner Production",
      content: "Our paper, \"A stepwise clustered ensemble temperature downscaling (SCETD) approach for assessing the future changes of temperature extremes over Yangtze River basin,\" has been accepted for publication in the Journal of Cleaner Production. This research introduces a new advanced statistical method to provide detailed insights into future temperature extremes in the Yangtze River basin, which is crucial for regional adaptation strategies.",
      icon: "fa-file-alt",
      color: "blue"
    },
    {
      title: "Research Accepted in Atmosphere",
      date: "2024-02",
      category: "Publication",
      journal: "Atmosphere",
      content: "We are pleased to announce the acceptance of our research, \"A Stepwise Multifactor Regression Analysis of the Interactive Effects of Multiple Climate Factors on the Response of Vegetation Recovery to Drought,\" in the journal Atmosphere. This study employs a novel regression analysis to unravel the complex ways in which climate factors interact to influence how vegetation recovers from drought conditions.",
      icon: "fa-file-alt",
      color: "cyan"
    },
    {
      title: "Publication in Climate Dynamics",
      date: "2024-01",
      category: "Publication",
      journal: "Climate Dynamics",
      content: "Our latest work, \"A stepwise-clustered copula downscaling approach for ensemble analyses of discrete and interactive features in precipitation-extreme variations: a case study for eastern China,\" has been accepted for publication in Climate Dynamics. This paper introduces a sophisticated statistical approach to analyze the intricate patterns and interactions within extreme precipitation events in eastern China, enhancing our understanding of regional climate risks.",
      icon: "fa-file-alt",
      color: "teal"
    },
    {
      title: "Research Published in Geophysical Research Letters",
      date: "2023-12",
      category: "Publication",
      journal: "Geophysical Research Letters",
      content: "Thrilled to share that our paper, \"Interactions between anthropogenic greenhouse-gas and aerosol emissions will shape extreme precipitations over the Qinghai-Tibet Plateau,\" has been accepted by Geophysical Research Letters. This groundbreaking research reveals the significant and interactive roles of greenhouse gas and aerosol emissions from human activities in shaping the future of extreme precipitation events over the sensitive Qinghai-Tibet Plateau.",
      icon: "fa-file-alt",
      color: "blue"
    },
    {
      title: "Paper Accepted in Atmosphere",
      date: "2023-11",
      category: "Publication",
      journal: "Atmosphere",
      content: "We're happy to announce the acceptance of our paper, \"Multilevel Drought-Induced Resistance and Resilience Analysis for Vegetation in the Yellow River Basin,\" in Atmosphere. This comprehensive analysis investigates how vegetation in the Yellow River Basin resists and recovers from drought events, providing valuable insights for ecosystem management.",
      icon: "fa-file-alt",
      color: "cyan"
    },
    {
      title: "Research in Climate Dynamics",
      date: "2023-10",
      category: "Publication",
      journal: "Climate Dynamics",
      content: "Our research, \"Multilevel factorial analysis for effects of SSPs and GCMs on regional climate change: a case study for the Yangtze River Basin,\" has been accepted for publication in Climate Dynamics. This paper quantifies the contributions of different future socioeconomic pathways and global climate models to regional climate change projections in the Yangtze River Basin.",
      icon: "fa-file-alt",
      color: "teal"
    },
    {
      title: "Publication in Journal of Hydrology",
      date: "2023-09",
      category: "Publication",
      journal: "Journal of Hydrology",
      content: "Our paper, \"Projections of compound wet-warm and dry-warm extreme events in summer over China,\" has been accepted for publication in the Journal of Hydrology. This study provides crucial projections of the co-occurrence of wet-warm and dry-warm extreme events during summer across China, highlighting potential risks to public health and agriculture.",
      icon: "fa-file-alt",
      color: "blue"
    },
    {
      title: "Research Accepted in Journal of Applied Meteorology and Climatology",
      date: "2023-08",
      category: "Publication",
      journal: "Journal of Applied Meteorology and Climatology",
      content: "We are excited to share that our research, \"Quantile delta-mapped spatial disaggregation analysis for climate variations over the Yangtze River Basin,\" has been accepted by the Journal of Applied Meteorology and Climatology. This paper presents a new advanced statistical technique to provide detailed, spatially refined analysis of climate variations across the Yangtze River Basin.",
      icon: "fa-file-alt",
      color: "cyan"
    },
    {
      title: "Paper Published in Climate Dynamics",
      date: "2023-07",
      category: "Publication",
      journal: "Climate Dynamics",
      content: "Our paper, \"Quantile delta-mapped spatial disaggregation analysis for summertime compound extremes over China,\" has been accepted for publication in Climate Dynamics. This work offers a detailed spatial analysis of compound climate extremes occurring in summertime over China, utilizing a sophisticated statistical method.",
      icon: "fa-file-alt",
      color: "teal"
    },
    {
      title: "Study Accepted in Journal of Environmental Management",
      date: "2023-06",
      category: "Publication",
      journal: "Journal of Environmental Management",
      content: "We're pleased to announce the acceptance of our study, \"Spatiotemporal geographically weighted regression analysis for runoff variations in the Weihe River Basin,\" in the Journal of Environmental Management. This research uses advanced spatial statistics to analyze the changing patterns of runoff in the Weihe River Basin over time, identifying key influencing factors.",
      icon: "fa-file-alt",
      color: "blue"
    },
    {
      title: "Research in Communications Earth & Environment",
      date: "2023-05",
      category: "Publication",
      journal: "Communications Earth & Environment",
      content: "Our research, \"The increasing water stress projected for China could shift the agriculture and manufacturing industry geographically,\" has been accepted for publication in Communications Earth & Environment. This paper highlights the potential for increasing water stress across China to lead to substantial geographical shifts in the agriculture and manufacturing sectors.",
      icon: "fa-file-alt",
      color: "cyan"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-cyan-50 to-slate-50">
        {/* Animated Background Blobs */}
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl font-extrabold mb-6">
              <span className="block bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-500 bg-clip-text text-transparent">
                News & Updates
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stay informed about our latest research publications, achievements, and developments
            </p>
          </div>
        </div>
      </div>

      {/* News Timeline */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="space-y-8">
          {newsItems.map((item, index) => (
            <div key={index} className="relative">
              {/* Timeline Line */}
              {index < newsItems.length - 1 && (
                <div className="absolute left-8 top-20 bottom-0 w-0.5 bg-gradient-to-b from-blue-200 to-transparent"></div>
              )}

              {/* News Card */}
              <div className="flex gap-6">
                {/* Date Badge */}
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 shadow-lg">
                    <img
                      src={`https://api.iconify.design/mdi:${
                        item.color === 'blue' ? 'file-document' :
                        item.color === 'cyan' ? 'file-document' :
                        item.color === 'teal' ? 'file-document' : 'file-document'
                      }.svg?color=${
                        item.color === 'blue' ? '%233b82f6' :
                        item.color === 'cyan' ? '%2306b6d4' :
                        item.color === 'teal' ? '%2314b8a6' : '%233b82f6'
                      }&width=64&height=64`}
                      alt="Publication"
                      className="w-full h-full"
                    />
                  </div>
                </div>

                {/* Content Card */}
                <div className="flex-1 bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden">
                  <div className="p-8">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="inline-block px-3 py-1 bg-blue-100 text-blue-600 text-sm font-semibold rounded-full">
                            {item.category}
                          </span>
                          <span className="text-sm text-gray-500 flex items-center">
                            <img
                              src="https://api.iconify.design/mdi:calendar.svg?color=%236b7280&width=16&height=16"
                              alt="Date"
                              className="w-4 h-4 mr-2"
                            />
                            {item.date}
                          </span>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                          {item.title}
                        </h3>
                        <p className="text-sm font-medium text-blue-600 flex items-center">
                          <img
                            src="https://api.iconify.design/mdi:book-open-page-variant.svg?color=%232563eb&width=16&height=16"
                            alt="Journal"
                            className="w-4 h-4 mr-2"
                          />
                          {item.journal}
                        </p>
                      </div>
                    </div>

                    {/* Content */}
                    <p className="text-gray-600 leading-relaxed">
                      {item.content}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ContactPage Component
function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [feedback, setFeedback] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();

    // Create mailto link
    const subject = encodeURIComponent('Contact from ' + formData.name);
    const body = encodeURIComponent(
      'Name: ' + formData.name + '\n' +
      'Email: ' + formData.email + '\n\n' +
      'Message:\n' + formData.message
    );

    // Open default email client
    window.location.href = 'mailto:support@hydroclimate.cn?subject=' + subject + '&body=' + body;

    // Show success message
    setFeedback('Your message has been prepared. Please send it from your email client.');

    // Clear form
    setFormData({ name: '', email: '', message: '' });

    // Clear feedback after 5 seconds
    setTimeout(() => setFeedback(''), 5000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Background */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-cyan-50 to-slate-50">
        {/* Animated Background Blobs */}
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl font-extrabold mb-6">
              <span className="block bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-500 bg-clip-text text-transparent">
                Contact Us
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We welcome collaborations, inquiries, and opportunities to advance hydroclimate research together
            </p>
          </div>
        </div>
      </div>

      {/* Contact Information Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 mb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Email Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-shadow duration-300 border border-gray-100">
            <div className="w-12 h-12 mb-6 mx-auto">
              <img
                src="https://api.iconify.design/mdi:email.svg?color=%233b82f6&width=48&height=48"
                alt="Email"
                className="w-full h-full"
              />
            </div>
            <h3 className="text-xl font-bold text-gray-900 text-center mb-3">Email Us</h3>
            <p className="text-gray-600 text-center mb-4">Send us an email anytime</p>
            <a href="mailto:support@hydroclimate.cn" className="block text-center text-blue-600 hover:text-blue-700 font-semibold">
              support@hydroclimate.cn
            </a>
          </div>

          {/* Location Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-shadow duration-300 border border-gray-100">
            <div className="w-12 h-12 mb-6 mx-auto">
              <img
                src="https://api.iconify.design/mdi:map-marker.svg?color=%2306b6d4&width=48&height=48"
                alt="Location"
                className="w-full h-full"
              />
            </div>
            <h3 className="text-xl font-bold text-gray-900 text-center mb-3">Visit Us</h3>
            <p className="text-gray-600 text-center leading-relaxed">
              No.19 Xinjiekouwai Street<br />
              Haidian District, Beijing<br />
              China 100875
            </p>
          </div>

          {/* Institution Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-shadow duration-300 border border-gray-100">
            <div className="w-12 h-12 mb-6 mx-auto">
              <img
                src="https://api.iconify.design/mdi:school.svg?color=%2314b8a6&width=48&height=48"
                alt="Institution"
                className="w-full h-full"
              />
            </div>
            <h3 className="text-xl font-bold text-gray-900 text-center mb-3">Institution</h3>
            <p className="text-gray-600 text-center leading-relaxed">
              School of Environment<br />
              Beijing Normal University
            </p>
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <div className="bg-gradient-to-br from-gray-50 to-blue-50 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Send Us a Message</h2>
            <p className="text-xl text-gray-600">
              Have questions about our research or interested in collaboration? We'd love to hear from you.
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Your Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="john@example.com"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="6"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
                  placeholder="Tell us about your inquiry or collaboration interest..."
                  required
                ></textarea>
              </div>
              <div className="text-center">
                <button
                  type="submit"
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  Send Message
                </button>
              </div>
            </form>
            {feedback && (
              <div className="mt-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
                <p className="text-green-800 font-medium">{feedback}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Campus Environment Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Our Campus
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Located at Beijing Normal University, our lab benefits from a vibrant academic environment and world-class facilities
            </p>
          </div>

          {/* Main Campus Building */}
          <div className="mb-12">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="/images/campus/DSCF1507.JPG"
                alt="Beijing Normal University Main Building"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">Beijing Normal University</h3>
                <p className="text-lg text-gray-200 max-w-3xl">
                  A leading research institution with over a century of excellence in education and innovation,
                  providing an inspiring environment for cutting-edge hydroclimate research.
                </p>
              </div>
            </div>
          </div>

          {/* Photo Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {/* Photo 1: Bell Tower */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group">
              <img
                src="/images/campus/DSCF1515.JPG"
                alt="Century Memorial Bell"
                className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <p className="text-white font-bold text-lg">Century Memorial Bell</p>
                <p className="text-gray-200 text-sm">A symbol of academic heritage</p>
              </div>
            </div>

            {/* Photo 2: Confucius Statue */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group">
              <img
                src="/images/campus/DSCF1552.JPG"
                alt="Confucius Statue"
                className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <p className="text-white font-bold text-lg">Heritage and Wisdom</p>
                <p className="text-gray-200 text-sm">Honoring scholarly tradition</p>
              </div>
            </div>

            {/* Photo 3: Campus Garden */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group">
              <img
                src="/images/campus/DSCF1551.JPG"
                alt="Campus Garden"
                className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <p className="text-white font-bold text-lg">Beautiful Campus Gardens</p>
                <p className="text-gray-200 text-sm">Inspiring green spaces</p>
              </div>
            </div>

            {/* Photo 4: Autumn Foliage */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group">
              <img
                src="/images/campus/DSCF1547.JPG"
                alt="Autumn Campus"
                className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <p className="text-white font-bold text-lg">Seasonal Beauty</p>
                <p className="text-gray-200 text-sm">Autumn colors on campus</p>
              </div>
            </div>

            {/* Photo 5: Ginkgo Trees */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group">
              <img
                src="/images/campus/DSCF1605.JPG"
                alt="Ginkgo Trees"
                className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <p className="text-white font-bold text-lg">Historic Ginkgo Avenue</p>
                <p className="text-gray-200 text-sm">Golden autumn spectacle</p>
              </div>
            </div>

            {/* Photo 6: Academic Building */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group">
              <img
                src="/images/campus/DSCF1594.JPG"
                alt="Academic Building"
                className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <p className="text-white font-bold text-lg">Modern Research Facilities</p>
                <p className="text-gray-200 text-sm">State-of-the-art infrastructure</p>
              </div>
            </div>
          </div>

          {/* Facilities and Location Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Location Details */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-8 border border-blue-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 mr-4">
                  <img
                    src="https://api.iconify.design/mdi:map-marker.svg?color=%233b82f6&width=48&height=48"
                    alt="Location"
                    className="w-full h-full"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Location</h3>
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Our laboratory is located within the School of Environment at Beijing Normal University,
                one of China's most prestigious institutions for environmental science and education. The campus
                provides an ideal setting for interdisciplinary research and collaboration.
              </p>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-start">
                  <div className="w-5 h-5 mt-1 mr-3 flex-shrink-0">
                    <img
                      src="https://api.iconify.design/mdi:office-building.svg?color=%233b82f6&width=20&height=20"
                      alt="Institution"
                      className="w-full h-full"
                    />
                  </div>
                  <div>
                    <p className="font-semibold">Institution</p>
                    <p>School of Environment, Beijing Normal University</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-5 h-5 mt-1 mr-3 flex-shrink-0">
                    <img
                      src="https://api.iconify.design/mdi:map-marker.svg?color=%233b82f6&width=20&height=20"
                      alt="Address"
                      className="w-full h-full"
                    />
                  </div>
                  <div>
                    <p className="font-semibold">Address</p>
                    <p>No.19 Xinjiekouwai Street, Haidian District, Beijing, China 100875</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Facilities */}
            <div className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-3xl p-8 border border-cyan-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 mr-4">
                  <img
                    src="https://api.iconify.design/mdi:flask-outline.svg?color=%2306b6d4&width=48&height=48"
                    alt="Research Facilities"
                    className="w-full h-full"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Research Facilities</h3>
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Our lab is equipped with cutting-edge technology and resources to support advanced hydroclimate research:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="w-6 h-6 mr-3 mt-0.5 flex-shrink-0">
                    <img
                      src="https://api.iconify.design/mdi:check-circle.svg?color=%2306b6d4&width=24&height=24"
                      alt="Check"
                      className="w-full h-full"
                    />
                  </div>
                  <span className="text-gray-700">High-performance computing clusters for climate modeling and simulation</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 mr-3 mt-0.5 flex-shrink-0">
                    <img
                      src="https://api.iconify.design/mdi:check-circle.svg?color=%2306b6d4&width=24&height=24"
                      alt="Check"
                      className="w-full h-full"
                    />
                  </div>
                  <span className="text-gray-700">Advanced data analysis and visualization laboratories</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 mr-3 mt-0.5 flex-shrink-0">
                    <img
                      src="https://api.iconify.design/mdi:check-circle.svg?color=%2306b6d4&width=24&height=24"
                      alt="Check"
                      className="w-full h-full"
                    />
                  </div>
                  <span className="text-gray-700">Collaborative research spaces for interdisciplinary teamwork</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 mr-3 mt-0.5 flex-shrink-0">
                    <img
                      src="https://api.iconify.design/mdi:check-circle.svg?color=%2306b6d4&width=24&height=24"
                      alt="Check"
                      className="w-full h-full"
                    />
                  </div>
                  <span className="text-gray-700">Access to extensive climate and hydrological databases</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <div className="bg-gradient-to-br from-gray-50 to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Find Us</h2>
            <p className="text-xl text-gray-600">
              Located in the heart of Beijing's academic district
            </p>
          </div>
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="w-full h-[500px] relative">
              {/* ArcGIS Map Embed */}
              <iframe
                title="Beijing Normal University Location"
                src="https://www.arcgis.com/apps/Embed/index.html?webmap=67372ff42cd145319639a99152b15bc3&extent=116.3465,39.9524,116.3865,39.9724&zoom=true&scale=true&legend=true&disable_scroll=false&theme=light"
                width="100%"
                height="100%"
                style={{border: 0, overflow: 'hidden'}}
              ></iframe>
              <div className="absolute bottom-4 right-4 bg-white px-4 py-2 rounded-lg shadow-lg">
                <a
                  href="https://www.arcgis.com/home/webmap/viewer.html?center=116.3665,39.9624&level=16"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-2"
                >
                  <img
                    src="https://api.iconify.design/mdi:open-in-new.svg?color=%232563eb&width=16&height=16"
                    alt="Open"
                    className="w-4 h-4"
                  />
                  View on ArcGIS
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main App component with Router wrapper
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
